import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Divider,
  Spinner,
  Center,
  useColorModeValue,
  Card,
  CardHeader,
  CardBody,
  Switch,
  Checkbox,
} from '@chakra-ui/react'
import { AddIcon, EditIcon, DeleteIcon, ViewIcon } from '@chakra-ui/icons'
import axios from 'axios'
import { hasPermission } from '../config/permissions'
import RichTextEditor from '../components/RichTextEditor'

interface Announcement {
  id: number
  title: string
  content: string
  author: string
  is_popup: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

const AnnouncementPage: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  })
  
  // 폼 상태
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    is_popup: false,
    is_active: true
  })
  const [editorKey, setEditorKey] = useState(0)
  
  // 모달 제어
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  
  const toast = useToast()
  const cancelRef = useRef<HTMLButtonElement>(null)
  
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  
  // 현재 사용자 정보
  const userRole = (localStorage.getItem('userRole') || 'admin') as 'admin' | 'bexco'

  // 수정 모달 닫기 (폼 초기화)
  const handleEditClose = () => {
    setFormData({ title: '', content: '', is_popup: false, is_active: true })
    setSelectedAnnouncement(null)
    onEditClose()
  }

  // 등록 모달 닫기 (폼 초기화)
  const handleCreateClose = () => {
    setFormData({ title: '', content: '', is_popup: false, is_active: true })
    onCreateClose()
  }

  // 목록 조회
  const fetchAnnouncements = async (page = 1) => {
    setLoading(true)
    try {
      const response = await axios.get('/graph/announcements', {
        params: { page, limit: 10 }
      })
      setAnnouncements(response.data.data)
      setPagination(response.data.pagination)
    } catch (error: any) {
      toast({
        title: '목록 조회 실패',
        description: error.response?.data?.message || '목록을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  // 상세 조회
  const fetchAnnouncementDetail = async (id: number) => {
    try {
      const response = await axios.get(`/graph/announcements/${id}`)
      setSelectedAnnouncement(response.data.data)
      onViewOpen()
    } catch (error: any) {
      toast({
        title: '조회 실패',
        description: error.response?.data?.message || '상세 정보를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // 등록
  const handleCreate = async () => {
    if (!formData.title || !formData.content) {
      toast({
        title: '입력 오류',
        description: '제목과 내용을 모두 입력해주세요.',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    try {
      await axios.post('/graph/announcements', formData)
      toast({
        title: '등록 완료',
        description: '공지사항이 성공적으로 등록되었습니다.',
        status: 'success',
        duration: 3000,
      })
      fetchAnnouncements()
      handleCreateClose()
    } catch (error: any) {
      toast({
        title: '등록 실패',
        description: error.response?.data?.message || '등록에 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // 수정
  const handleEdit = async () => {
    if (!selectedAnnouncement) return

    try {
      await axios.put(`/graph/announcements/${selectedAnnouncement.id}`, formData)
      toast({
        title: '수정 완료',
        description: '공지사항이 수정되었습니다.',
        status: 'success',
        duration: 3000,
      })
      fetchAnnouncements()
      if (isViewOpen) {
        fetchAnnouncementDetail(selectedAnnouncement.id)
      }
      handleEditClose()
    } catch (error: any) {
      toast({
        title: '수정 실패',
        description: error.response?.data?.message || '수정에 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  // 삭제
  const handleDelete = async () => {
    if (!selectedAnnouncement) return

    try {
      await axios.delete(`/graph/announcements/${selectedAnnouncement.id}`)
      toast({
        title: '삭제 완료',
        description: '공지사항이 삭제되었습니다.',
        status: 'success',
        duration: 3000,
      })
      onDeleteClose()
      onViewClose()
      fetchAnnouncements()
    } catch (error: any) {
      toast({
        title: '삭제 실패',
        description: error.response?.data?.message || '삭제에 실패했습니다.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  useEffect(() => {
    fetchAnnouncements()
  }, [])

  // 수정 모달이 열릴 때 폼 데이터 설정
  useEffect(() => {
    if (isEditOpen && selectedAnnouncement) {
      setFormData({
        title: selectedAnnouncement.title || '',
        content: selectedAnnouncement.content || '',
        is_popup: selectedAnnouncement.is_popup || false,
        is_active: selectedAnnouncement.is_active !== false
      })
      setEditorKey(prev => prev + 1)
    }
  }, [isEditOpen, selectedAnnouncement])

  const canManage = hasPermission(userRole, 'ANNOUNCEMENT_CREATE')

  return (
    <Box p={8}>
      {/* 페이지 헤더 */}
      <Box mb={6}>
        <Heading size="lg" color="blue.700" mb={2}>
          공지사항
        </Heading>
        <Text color="gray.600">
          시스템 공지사항을 확인하고 관리할 수 있습니다.
        </Text>
      </Box>
      
      <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
        <CardHeader>
          <HStack justify="flex-end" spacing={4}>
            {canManage && (
              <Button
                leftIcon={<AddIcon />}
                colorScheme="bexco"
                minW="120px"
                size="md"
                onClick={() => {
                  setFormData({ title: '', content: '', is_popup: false, is_active: true })
                  onCreateOpen()
                }}
              >
                공지 등록
              </Button>
            )}
          </HStack>
        </CardHeader>

        <CardBody pt={0}>
          {loading ? (
            <Center py={10}>
              <Spinner size="xl" color="bexco.500" />
            </Center>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>번호</Th>
                    <Th>제목</Th>
                    <Th>작성자</Th>
                    <Th>팝업</Th>
                    <Th>상태</Th>
                    <Th>작성일</Th>
                    {canManage && <Th>작업</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {announcements.map((announcement) => (
                    <Tr key={announcement.id} _hover={{ bg: 'gray.50' }}>
                      <Td>{announcement.id}</Td>
                      <Td
                        maxW="300px"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        cursor="pointer"
                        onClick={() => fetchAnnouncementDetail(announcement.id)}
                        _hover={{ color: 'bexco.500', textDecoration: 'underline' }}
                      >
                        {announcement.title}
                      </Td>
                      <Td>{announcement.author}</Td>
                      <Td>
                        {announcement.is_popup ? (
                          <Badge colorScheme="orange">팝업</Badge>
                        ) : (
                          <Badge colorScheme="gray">일반</Badge>
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={announcement.is_active ? 'green' : 'gray'}>
                          {announcement.is_active ? '활성' : '비활성'}
                        </Badge>
                      </Td>
                      <Td>{new Date(announcement.created_at).toLocaleDateString()}</Td>
                      {canManage && (
                        <Td>
                          <HStack spacing={2}>
                            <IconButton
                              aria-label="상세보기"
                              icon={<ViewIcon />}
                              size="sm"
                              onClick={() => fetchAnnouncementDetail(announcement.id)}
                            />
                            <IconButton
                              aria-label="수정"
                              icon={<EditIcon />}
                              size="sm"
                              colorScheme="blue"
                              onClick={async () => {
                                try {
                                  const response = await axios.get(`/graph/announcements/${announcement.id}`)
                                  setSelectedAnnouncement(response.data.data)
                                  onEditOpen()
                                } catch (error: any) {
                                  toast({
                                    title: '조회 실패',
                                    description: '공지사항 정보를 불러오는데 실패했습니다.',
                                    status: 'error',
                                    duration: 3000,
                                  })
                                }
                              }}
                            />
                            <IconButton
                              aria-label="삭제"
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              onClick={() => {
                                setSelectedAnnouncement(announcement)
                                onDeleteOpen()
                              }}
                            />
                          </HStack>
                        </Td>
                      )}
                    </Tr>
                  ))}
                  {announcements.length === 0 && (
                    <Tr>
                      <Td colSpan={canManage ? 7 : 6} textAlign="center" py={8} color="gray.500">
                        등록된 공지사항이 없습니다.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          )}

          {/* 페이지네이션 */}
          {pagination.totalPages > 1 && (
            <Flex justify="center" mt={4} gap={2}>
              <Button
                size="sm"
                onClick={() => fetchAnnouncements(pagination.page - 1)}
                isDisabled={pagination.page <= 1}
              >
                이전
              </Button>
              <Text alignSelf="center" fontSize="sm">
                {pagination.page} / {pagination.totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() => fetchAnnouncements(pagination.page + 1)}
                isDisabled={pagination.page >= pagination.totalPages}
              >
                다음
              </Button>
            </Flex>
          )}
        </CardBody>
      </Card>

      {/* 등록 모달 */}
      <Modal isOpen={isCreateOpen} onClose={handleCreateClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>공지사항 등록</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>제목</FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="제목을 입력하세요"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>내용</FormLabel>
                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => setFormData({ ...formData, content: value })}
                  placeholder="내용을 입력하세요..."
                  height="400px"
                />
              </FormControl>
              <HStack w="full" spacing={8}>
                <FormControl display="flex" alignItems="center">
                  <Checkbox
                    isChecked={formData.is_popup}
                    onChange={(e) => setFormData({ ...formData, is_popup: e.target.checked })}
                    colorScheme="orange"
                  >
                    <Text fontSize="sm">로그인 시 팝업으로 표시</Text>
                  </Checkbox>
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="is_active" mb="0" fontSize="sm">
                    활성화
                  </FormLabel>
                  <Switch
                    id="is_active"
                    isChecked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    colorScheme="green"
                  />
                </FormControl>
              </HStack>
              <Flex w="full" justify="flex-end" gap={2}>
                <Button onClick={handleCreateClose}>취소</Button>
                <Button colorScheme="bexco" onClick={handleCreate}>등록</Button>
              </Flex>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 수정 모달 */}
      <Modal isOpen={isEditOpen} onClose={handleEditClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>공지사항 수정</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isEditOpen && (
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>제목</FormLabel>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>내용</FormLabel>
                  <RichTextEditor
                    key={`edit-${editorKey}`}
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    placeholder="내용을 입력하세요..."
                    height="400px"
                  />
                </FormControl>
                <HStack w="full" spacing={8}>
                  <FormControl display="flex" alignItems="center">
                    <Checkbox
                      isChecked={formData.is_popup}
                      onChange={(e) => setFormData({ ...formData, is_popup: e.target.checked })}
                      colorScheme="orange"
                    >
                      <Text fontSize="sm">로그인 시 팝업으로 표시</Text>
                    </Checkbox>
                  </FormControl>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="is_active_edit" mb="0" fontSize="sm">
                      활성화
                    </FormLabel>
                    <Switch
                      id="is_active_edit"
                      isChecked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      colorScheme="green"
                    />
                  </FormControl>
                </HStack>
                <Flex w="full" justify="flex-end" gap={2}>
                  <Button onClick={handleEditClose}>취소</Button>
                  <Button colorScheme="blue" onClick={handleEdit}>수정</Button>
                </Flex>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 상세보기 모달 */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>공지사항 상세</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedAnnouncement && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Heading size="md">{selectedAnnouncement.title}</Heading>
                    <HStack>
                      {selectedAnnouncement.is_popup && (
                        <Badge colorScheme="orange">팝업</Badge>
                      )}
                      <Badge colorScheme={selectedAnnouncement.is_active ? 'green' : 'gray'}>
                        {selectedAnnouncement.is_active ? '활성' : '비활성'}
                      </Badge>
                    </HStack>
                  </HStack>
                  <HStack fontSize="sm" color="gray.600" spacing={4}>
                    <Text>작성자: {selectedAnnouncement.author}</Text>
                    <Text>작성일: {new Date(selectedAnnouncement.created_at).toLocaleString()}</Text>
                  </HStack>
                </Box>

                <Divider />

                <Box
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  minH="200px"
                  dangerouslySetInnerHTML={{ __html: selectedAnnouncement.content }}
                  sx={{
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                      margin: '10px 0',
                      borderRadius: 'md',
                    },
                    '& p': {
                      marginBottom: '10px',
                    },
                  }}
                />
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>공지사항 삭제</AlertDialogHeader>
            <AlertDialogBody>
              정말로 이 공지사항을 삭제하시겠습니까?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                취소
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                삭제
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default AnnouncementPage
